'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
  details: string[];
}

export default function AmenitiesServicesSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: 'What amenities are included in my apartment?',
      answer: 'All apartments include fully equipped kitchen, high-speed WiFi, smart TV, air conditioning, and premium bedding.',
      details: [
        'Fully equipped kitchen with refrigerator, microwave, induction cooktop, and cookware, on request',
        'High-speed WiFi (100 Mbps) with backup connectivity',
        'Smart TV with streaming services and cable channels',
        'Air conditioning with individual climate control',
        'Premium bedding with hypoallergenic pillows and linens',
        'Work desk with ergonomic chair and power outlets',
      ],
    },
    {
      question: 'Is housekeeping service provided?',
      answer: 'Yes, complimentary housekeeping is provided based on your stay duration with flexible scheduling options.',
      details: [
        'Daily housekeeping for stays up to 7 days',
        'Alternate-day service for stays 8-30 days',
        'Twice-weekly service for monthly stays (30+ days)',
        'Additional cleaning available on request (charges apply)',
        'Linen and towel change included with each service',
      ],
    },
    {
      question: 'Do you provide laundry services?',
      answer: 'Yes, we offer both self-service laundry facilities and professional laundry services.',
      details: [
        'Professional laundry service with 24-hour turnaround',
        'Dry cleaning available through partner service',
        'Iron and ironing board available in all apartments',
      ],
    },
    {
      question: 'Is parking available?',
      answer: 'Complimentary off site street parking available for all guests.',
      details: [
        'Complimentary off site street parking',
        'Located just outside the property',
        'Secure parking area with CCTV surveillance',
        'No parking charges',
        'Suitable for cars and two wheelers',
      ],
    },
    {
      question: 'Can I request additional services?',
      answer: 'Yes, we offer various additional services including airport transfers, grocery delivery, and concierge assistance.',
      details: [
        'Airport pickup/drop service (advance booking required)',
        'Pre-arrival grocery stocking service',
        'Restaurant reservations and local recommendations',
        'Tour and activity booking assistance',
        'Babysitting services (through verified partners)',
        'Pet care services for approved pets',
      ],
    },
    {
      question: 'Is there a fitness center or gym?',
      answer: 'Free and paid access to fully equipped off site fitness center.',
      details: [
        'Free and paid access to fully equipped off site fitness center',
        'Cardio equipment: treadmills, ellipticals, stationary bikes',
        'Strength training equipment and free weights',
        'Yoga mats and meditation space',
        'Complimentary access for LUXOS guests',
      ],
    },
  ];

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="amenities-services" className="py-16 lg:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="SparklesIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Amenities & Services</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Facilities & Services
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Learn about our comprehensive amenities, housekeeping services, and additional offerings to enhance your stay.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-display text-xl font-semibold text-primary mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-text-secondary text-sm">{faq.answer}</p>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 animate-slide-in-left">
                  <div className="border-t border-border pt-4">
                    <ul className="space-y-3">
                      {faq.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-accent flex-shrink-0 mt-0.5"
                          />
                          <span className="text-text-secondary text-sm leading-relaxed">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}